/**
 * Small HTML builders shared by the case-report sections. Every value comes
 * from the payload: an absent field prints "not available", nothing is
 * invented, totals are per token (R15).
 */
import { esc, monoSpan, fmtTs } from '../../utils/reportCommon';
import { formatAmount, formatAmountsByToken, toEpochMs } from '../../services/caseCanvas';

export type Rec = Record<string, unknown>;

export const NA = '<span class="na">not available</span>';
export const NOT_RUN = (what: string) =>
  `<p class="not-run">Not run: the case-worker run in this report did not produce ${esc(what)}.</p>`;

export const isRec = (v: unknown): v is Rec => !!v && typeof v === 'object' && !Array.isArray(v);
export const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
export const present = (v: unknown) => v !== undefined && v !== null && v !== '';
export const humanKey = (k: string) => k.replace(/^_+/, '').replace(/_/g, ' ');

/** True when a value carries data: not null/empty, not an empty list or object. */
export function hasData(v: unknown): boolean {
  if (!present(v)) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (isRec(v)) return Object.keys(v).length > 0;
  return true;
}

/** Escaped text, or "not available". */
export function txt(v: unknown): string {
  return present(v) ? esc(String(v)) : NA;
}

export function amountText(value: unknown, token: unknown): string {
  if (!isNum(value)) return NA;
  return esc(`${formatAmount(value)}${present(token) ? ` ${String(token)}` : ''}`);
}

export function tsText(v: unknown): string {
  return present(v) && toEpochMs(v) !== null ? esc(fmtTs(v)) : (present(v) ? esc(String(v)) : NA);
}

export function hashText(v: unknown): string {
  return present(v) ? monoSpan(String(v)) : NA;
}

export function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

const HASHY = /(hash|address|wallet|^from$|^to$|source|target|receiver|sender|delegat|owner)/i;
const TIMEY = /(^ts$|time|timestamp|_at$|^date)/i;

/** One table cell for an arbitrary value from the run payload. */
export function cell(key: string, v: unknown): string {
  if (!present(v)) return /^(usd|usd_today|value_usd)$/.test(key) ? 'no price' : NA;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (isNum(v)) return TIMEY.test(key) ? tsText(v) : (/^(usd|usd_today|value_usd)$/.test(key) ? `$${esc(formatAmount(v))}` : esc(formatAmount(v)));
  if (typeof v === 'string') {
    if (HASHY.test(key) && !/\s/.test(v)) return monoSpan(v);
    if (TIMEY.test(key)) return tsText(v);
    return esc(v);
  }
  if (Array.isArray(v)) {
    if (!v.length) return NA;
    if (v.every((x) => typeof x === 'string' || isNum(x)) && v.length <= 6) return esc(v.join(', '));
    return esc(`${v.length} item(s)`);
  }
  if (isRec(v) && /_by_token$/.test(key)) {
    return esc(formatAmountsByToken(v as Record<string, number>)) || NA;
  }
  return `<code class="json">${esc(JSON.stringify(v))}</code>`;
}

/** Table over a list of records: preferred columns first, then the most common scalar keys. */
export function recordsTable(rows: unknown[], preferred: string[] = [], maxCols = 9): string {
  const recs = rows.filter(isRec);
  if (!recs.length) return '<p class="muted">none</p>';
  const freq = new Map<string, number>();
  for (const r of recs) for (const k of Object.keys(r)) freq.set(k, (freq.get(k) || 0) + 1);
  const cols = [
    ...preferred.filter((k) => freq.has(k)),
    ...[...freq.entries()].filter(([k]) => !preferred.includes(k)).sort((a, b) => b[1] - a[1]).map(([k]) => k),
  ].slice(0, maxCols);
  const head = cols.map((c) => `<th>${esc(humanKey(c))}</th>`).join('');
  const body = recs
    .map((r, i) => `<tr><td>${i + 1}</td>${cols.map((c) => `<td>${cell(c, r[c])}</td>`).join('')}</tr>`)
    .join('');
  return `<table class="data"><thead><tr><th>#</th>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

export function kvTable(obj: Rec, skip: Set<string> = new Set()): string {
  const rows = Object.entries(obj)
    .filter(([k, v]) => !skip.has(k) && (v === null || typeof v !== 'object' || Array.isArray(v) || /_by_token$/.test(k)))
    .map(([k, v]) => `<tr><td class="label">${esc(humanKey(k))}</td><td class="value">${cell(k, v)}</td></tr>`);
  return rows.length ? `<table class="meta-table">${rows.join('')}</table>` : '';
}

/** A list, a record of lists, or a record, as tables (first list key that exists wins). */
export function anyTable(v: unknown, keys: string[] = [], preferred: string[] = [], limit = 200): string {
  const list = listOf(v, ...keys);
  if (list) return recordsTable(list.slice(0, limit), preferred);
  if (isRec(v)) {
    const lists = Object.entries(v).filter(([, x]) => Array.isArray(x) && (x as unknown[]).some(isRec));
    const head = kvTable(v);
    return head + lists.map(([k, x]) => `<h4>${esc(humanKey(k))} (${(x as unknown[]).length})</h4>${recordsTable((x as unknown[]).slice(0, limit), preferred)}`).join('')
      || '<p class="muted">none</p>';
  }
  return present(v) ? `<p>${esc(String(v))}</p>` : '<p class="muted">none</p>';
}

/** Window / sample / truncated, printed next to every total of a profile. */
export function totalsContext(p: Rec): string {
  const days = isNum(p.window_days) ? ` (${formatAmount(p.window_days)} d)` : '';
  const span = present(p.window_start) || present(p.window_end)
    ? `${present(p.window_start) ? fmtTs(p.window_start) : '?'} to ${present(p.window_end) ? fmtTs(p.window_end) : '?'}${days}`
    : undefined;
  const win = span ?? p.declared_window ?? p.window ?? (present(p.window_days) ? `${p.window_days} d` : undefined);
  const sample = p.sample_size ?? p.tx_count ?? p.n_txs ?? p.transactions_examined;
  const trunc = p.truncated ?? p.is_truncated;
  const winText = isRec(win) ? JSON.stringify(win) : win;
  return `window: ${present(winText) ? String(winText) : 'not available'}; `
    + `sample: ${present(sample) ? String(sample) : 'not available'}; `
    + `truncated: ${typeof trunc === 'boolean' ? (trunc ? 'yes' : 'no') : 'not available'}`;
}

const PROFILE_META_KEYS = new Set([
  'declared_window', 'window', 'window_days', 'window_start', 'window_end',
  'sample_size', 'tx_count', 'n_txs', 'transactions_examined', 'truncated', 'is_truncated',
]);

/** {TOKEN: number} — a per-token total map (never summed across its keys). */
function isTokenMap(v: unknown): v is Record<string, number> {
  if (!isRec(v)) return false;
  const entries = Object.entries(v);
  return entries.length > 0 && entries.every(([k, x]) => isNum(x) && /^[A-Za-z0-9.+_-]{2,16}$/.test(k));
}

/** Total rows of a profile: `*_by_token` maps, `per_token` {TOK: {inflow, outflow}}, and `totals` maps. */
export function totalRows(p: Rec): { label: string; amounts: string; basis: string }[] {
  const out: { label: string; amounts: string; basis: string }[] = [];
  const ctx = totalsContext(p);
  for (const [k, v] of Object.entries(p)) {
    if (/_by_token$/.test(k) && isTokenMap(v)) out.push({ label: humanKey(k), amounts: formatAmountsByToken(v), basis: ctx });
  }
  if (isRec(p.per_token)) {
    for (const [tok, dirs] of Object.entries(p.per_token)) {
      if (!isRec(dirs)) continue;
      for (const dir of ['inflow', 'outflow']) {
        const st = dirs[dir];
        if (!isRec(st) || !isNum(st.total)) continue;
        const usd = isNum(st.usd_total) ? `; USD (from data): $${formatAmount(st.usd_total)}` : '';
        const cnt = isNum(st.count) ? `${st.count} transfer(s); ` : '';
        out.push({ label: `${dir} ${tok}`, amounts: `${formatAmount(st.total)} ${tok}`, basis: `${cnt}${ctx}${usd}` });
      }
    }
  }
  if (isRec(p.totals)) {
    const tctx = totalsContext({ ...p, ...p.totals });
    for (const [k, v] of Object.entries(p.totals)) {
      if (isTokenMap(v)) out.push({ label: humanKey(k), amounts: formatAmountsByToken(v), basis: tctx });
    }
  }
  return out;
}

export function profileBlock(p: Rec, i: number): string {
  const title = p.entity_name || p.label || p.name || p.address || `Profile ${i + 1}`;
  const totals = totalRows(p);
  const rowsHtml = totals.length
    ? totals.map((t) => `<tr><td>${esc(t.label)}</td><td>${esc(t.amounts) || NA}</td><td class="ctx">${esc(t.basis)}</td></tr>`).join('')
    : `<tr><td colspan="3">${NA} (no per-token totals in this profile)</td></tr>`;
  const skip = new Set([...PROFILE_META_KEYS, 'per_token', 'totals', 'caveats',
    ...Object.keys(p).filter((k) => /_by_token$/.test(k))]);
  const lists = Object.entries(p).filter(([k, v]) => Array.isArray(v) && v.some(isRec) && k !== 'caveats');
  const caveats = Array.isArray(p.caveats) ? p.caveats.map(String) : [];
  return `<div class="profile">
    <h3>${esc(String(title))}${present(p.address) && p.address !== title ? ` <span class="muted">${monoSpan(String(p.address))}</span>` : ''}</h3>
    <table class="data"><thead><tr><th>Total</th><th>Per token</th><th>Declared basis</th></tr></thead><tbody>${rowsHtml}</tbody></table>
    ${kvTable(p, skip)}
    ${caveats.length ? `<ul class="caveats">${caveats.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>` : ''}
    ${lists.map(([k, v]) => `<h4>${esc(humanKey(k))} (${(v as unknown[]).length})</h4>${recordsTable((v as unknown[]).slice(0, 25))}`).join('')}
  </div>`;
}

export function listOf(v: unknown, ...keys: string[]): unknown[] | null {
  if (Array.isArray(v)) return v;
  if (isRec(v)) {
    for (const k of keys) if (Array.isArray(v[k])) return v[k] as unknown[];
  }
  return null;
}

export function containerHead(c: unknown): string {
  if (!isRec(c)) return '';
  const caveats = Array.isArray(c.caveats) ? c.caveats.map(String) : [];
  const status = present(c.status) ? `<p>Status: <strong>${esc(String(c.status))}</strong>${present(c.not_tested_reason) ? ` (${esc(String(c.not_tested_reason))})` : ''}</p>` : '';
  return `${status}${caveats.length ? `<ul class="caveats">${caveats.map((x) => `<li>${esc(x)}</li>`).join('')}</ul>` : ''}`;
}
